import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Theme,
  Typography,
} from '@mui/material';
import { radius } from '../../../../theme';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { onSnapshot, getDoc, DocumentReference } from 'firebase/firestore';
import { useUsuario } from '../../../../providers/usuarioProvider';
import UploadDb from '../../../../db/uploadDb';
import UploadModel from '../../../../models/upload';
import { IconArrowLeft, IconCloudDownload } from '@tabler/icons-react';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';
import TabelaNotas from '../../../util/tabelas/tabelaNotas';
import ChipCorretora from '../../../util/ui/chipCorretora';
import ChipTipoUpload from '../../../util/ui/chipTipoUpload';
import ChipExtensao from '../../../util/ui/chipExtensao';
import NotaDb from '../../../../db/notaDb';
import { query, where } from 'firebase/firestore';
import { map } from 'lodash';
import { NotaBMF, NotaBovespa, NotaParcial } from '../../../../models/nota';
import ExercicioDireito from '../../../../models/exercicioDireito';
import Checkpoint from '../../../../models/checkpoint';
import TabelaExercicios from '../../../util/tabelas/tabelaExercicios';
import InteiroText from '../../../util/ui/inputs/inteiroText';
import getLabelTipoUpload from '../../../util/getLabelTipoUpload';
import { SituacaoUpload } from '../../../../models/enum';

/** Pendência de leitura → semântica `warn` de `palette.statusChip`. */
const sxChipWarn = (theme: Theme) => ({
  backgroundColor: theme.palette.statusChip.warn.bg,
  color: theme.palette.statusChip.warn.fg,
});

/** Situação do upload → uma das cinco semânticas de chip do DESIGN.md. */
function getSemanticaSituacao(
  situacao: string,
): 'positive' | 'accent' | 'destructive' {
  switch (situacao) {
    case SituacaoUpload.IMPORTADO:
      return 'positive';
    case SituacaoUpload.ERRO_AO_LER:
    case SituacaoUpload.ERRO_AO_IMPORTAR:
      return 'destructive';
    default:
      return 'accent';
  }
}

export default function Upload() {
  const { uploadId } = useParams();
  const navigate = useNavigate();
  const { visualizado } = useUsuario();
  const [upload, setUpload] = useState<UploadModel>();
  const [notas, setNotas] = useState<(NotaBovespa | NotaBMF)[]>([]);
  const [exercicios, setExercicios] = useState<ExercicioDireito[]>([]);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);

  useEffect(() => {
    if (!!visualizado && !!uploadId) {
      return onSnapshot(
        new UploadDb().doc({ usuarioId: visualizado, uploadId }),
        (snapshot) => {
          const up = snapshot.data() as UploadModel;
          setUpload(up);

          // Se upload tem itensImportados, resolve cada tipo
          if (up?.itensImportados) {
            const notasPromise: Promise<NotaBovespa | NotaBMF>[] = [];
            const exerciciosPromise: Promise<ExercicioDireito>[] = [];
            const checkpointsPromise: Promise<Checkpoint>[] = [];

            up.itensImportados.forEach((item) => {
              switch (item.parent.id) {
                case 'notas':
                  notasPromise.push(
                    getDoc(
                      item as DocumentReference<
                        NotaBovespa | NotaBMF | NotaParcial
                      >,
                    ).then((doc) => doc.data() as NotaBovespa | NotaBMF),
                  );
                  break;
                case 'exercicioDireito':
                  exerciciosPromise.push(
                    getDoc(item as DocumentReference<ExercicioDireito>).then(
                      (doc) => doc.data() as ExercicioDireito,
                    ),
                  );
                  break;
                case 'checkpoints':
                  checkpointsPromise.push(
                    getDoc(item as DocumentReference<Checkpoint>).then(
                      (doc) => doc.data() as Checkpoint,
                    ),
                  );
                  break;
              }
            });

            Promise.all(notasPromise).then((items) =>
              setNotas(
                items.filter((item): item is NotaBovespa | NotaBMF => !!item),
              ),
            );
            Promise.all(exerciciosPromise).then((items) =>
              setExercicios(
                items.filter((item): item is ExercicioDireito => !!item),
              ),
            );
            Promise.all(checkpointsPromise).then((items) =>
              setCheckpoints(
                items.filter((item): item is Checkpoint => !!item),
              ),
            );
          }
        },
      );
    }
  }, [visualizado, uploadId]);

  // Fallback para uploads antigos sem itensImportados
  useEffect(() => {
    if (!!visualizado && !!uploadId && upload && !!!upload.itensImportados) {
      return onSnapshot(
        query(
          new NotaDb().collection({ usuarioId: visualizado, notaId: '' }),
          where('upload', '==', uploadId),
        ),
        (snapshot) => {
          setNotas(
            map(snapshot.docs, (doc) => doc.data() as NotaBovespa | NotaBMF),
          );
        },
      );
    }
  }, [visualizado, uploadId, upload?.itensImportados]);

  const baixarArquivo = async () => {
    if (upload?.arquivo) {
      const url = await getDownloadURL(ref(getStorage(), upload.arquivo));
      window.open(url);
    }
  };

  if (!upload) return <></>;

  function getLabelSituacaoUpload(situacao: string): string {
    switch (situacao) {
      case SituacaoUpload.IMPORTADO:
        return 'Importado';
      case SituacaoUpload.SALVO:
        return 'Salvo';
      case SituacaoUpload.PENDENTE:
        return 'Pendente';
      case SituacaoUpload.ERRO_AO_LER:
        return 'Erro ao ler';
      case SituacaoUpload.ERRO_AO_IMPORTAR:
        return 'Erro ao importar';
      default:
        return situacao;
    }
  }

  return (
    <Container maxWidth="lg">
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
        pt={4}
        pb={2}
      >
        <Box>
          <Typography variant="eyebrow" component="p" color="text.secondary">
            Upload
          </Typography>
          <Typography variant="h1" mt={0.5}>
            {getLabelTipoUpload(upload.tipo)}
          </Typography>
          {upload.nomeArquivoOriginal && (
            <Typography
              variant="dataMono"
              component="p"
              color="text.secondary"
              mt={0.5}
              sx={{ fontSize: 13, wordBreak: 'break-all' }}
            >
              {upload.nomeArquivoOriginal}
            </Typography>
          )}

          <Box
            display="flex"
            flexDirection="row"
            gap={0.5}
            mt={0.5}
            flexWrap="wrap"
            alignItems="center"
          >
            {upload.periodo && (
              <Typography variant="dataMono" color="text.secondary">
                {upload.periodo.de.format('DD/MM/YY')} a{' '}
                {upload.periodo.a.format('DD/MM/YY')}
              </Typography>
            )}
            {upload.periodo && upload.dataUpload && (
              <Typography variant="body2" color="text.disabled">
                ·
              </Typography>
            )}
            {upload.dataUpload && (
              <Typography variant="body2" color="text.secondary">
                Importado em{' '}
                <Typography variant="dataMono" component="span">
                  {upload.dataUpload.format('DD/MM/YYYY')}
                </Typography>
              </Typography>
            )}
            {!!upload.quantidadePregoes && (
              <Typography variant="body2" color="text.secondary">
                ·{' '}
                <Typography variant="dataMono" component="span">
                  {upload.quantidadePregoes}
                </Typography>{' '}
                {upload.quantidadePregoes > 1 ? 'pregões' : 'pregão'}
              </Typography>
            )}
            {!!upload.quantidadeParciais && upload.quantidadeParciais > 0 && (
              <Chip
                label={`${upload.quantidadeParciais} nota não lida`}
                size="small"
                sx={sxChipWarn}
              />
            )}
            {!!upload.incompatibilidades &&
              upload.incompatibilidades.length > 0 && (
                <Chip
                  label={`${upload.incompatibilidades.length} ${upload.incompatibilidades.length > 1 ? 'incompatibilidades' : 'incompatibilidade'}`}
                  size="small"
                  sx={sxChipWarn}
                />
              )}
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            gap={1}
            mt={1}
            flexWrap="wrap"
            alignItems="center"
          >
            <ChipTipoUpload tipo={upload.tipo} />
            <Box display="inline-flex" alignItems="center">
              <ChipExtensao nomeArquivo={upload.nomeArquivoOriginal} />
            </Box>
            <Box display="inline-flex" alignItems="center">
              <ChipCorretora codigoCorretora={upload.corretora} size="small" />
            </Box>
            {upload.situacao && (
              <Chip
                label={getLabelSituacaoUpload(upload.situacao)}
                size="small"
                sx={(theme) => {
                  const tint =
                    theme.palette.statusChip[
                      getSemanticaSituacao(upload.situacao)
                    ];
                  return { backgroundColor: tint.bg, color: tint.fg };
                }}
              />
            )}
          </Box>
        </Box>
        <Box display="flex" flexDirection="row" gap={1}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => navigate(-1)}
            startIcon={<IconArrowLeft />}
          >
            Voltar
          </Button>
          {upload.arquivo && (
            <Button
              variant="contained"
              onClick={baixarArquivo}
              startIcon={<IconCloudDownload />}
            >
              Baixar arquivo
            </Button>
          )}
        </Box>
      </Box>

      {notas.length > 0 && (
        <Box pb={4}>
          <Paper variant="outlined" sx={{ borderRadius: `${radius.xl}px` }}>
            <Box p={2}>
              <Typography variant="h4" pb={1}>
                Notas importadas{' '}
                <Typography
                  variant="dataMono"
                  component="span"
                  color="text.secondary"
                >
                  ({notas.length})
                </Typography>
              </Typography>
              <TabelaNotas notas={notas} />
            </Box>
          </Paper>
        </Box>
      )}

      {exercicios.length > 0 && (
        <Box pb={4}>
          <Paper variant="outlined" sx={{ borderRadius: `${radius.xl}px` }}>
            <Box p={2}>
              <Typography variant="h4" pb={1}>
                Exercícios de Direito de Subscrição{' '}
                <Typography
                  variant="dataMono"
                  component="span"
                  color="text.secondary"
                >
                  ({exercicios.length})
                </Typography>
              </Typography>
              <TabelaExercicios exercicios={exercicios} />
            </Box>
          </Paper>
        </Box>
      )}

      {checkpoints.length > 0 && (
        <Box pb={4}>
          <Paper variant="outlined" sx={{ borderRadius: `${radius.xl}px` }}>
            <Box p={2}>
              <Typography variant="h4" pb={1}>
                Pontos de Verificação{' '}
                <Typography
                  variant="dataMono"
                  component="span"
                  color="text.secondary"
                >
                  ({checkpoints.length})
                </Typography>
              </Typography>
              <Grid container spacing={1}>
                {map(checkpoints, (checkpoint, index) => (
                  <Grid item xs={12} key={index}>
                    <Box
                      display="flex"
                      flexDirection="row"
                      gap={2}
                      alignItems="center"
                    >
                      <Typography
                        component={Link}
                        to={`/checkpoint#${checkpoint.data.format('YYYYMMDD')}`}
                        variant="dataMono"
                        sx={(theme) => ({
                          color: theme.palette.accentInk,
                          cursor: 'pointer',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        })}
                      >
                        {checkpoint.data.format('DD/MM/YYYY')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <InteiroText
                          value={checkpoint.posicoes.length}
                          displayType="text"
                        />{' '}
                        {checkpoint.posicoes.length > 1
                          ? 'posições importadas'
                          : 'posição importada'}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Box>
      )}
    </Container>
  );
}
